import { useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { localizedText } from '@/lib/localized-text'
import type { GroupRow } from '@/lib/platform'
import { useReactivateGroup } from './api'

/**
 * The confirmation shared by the groups table row menu and the group detail
 * header — both bring a suspended (or pending-purge) group back the same way,
 * so the dialog, the mutation and the success toast live once rather than
 * twice.
 */
export function ReactivateGroupDialog({
  group,
  onClose,
}: {
  group: GroupRow
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const reactivate = useReactivateGroup()
  const name = localizedText(group.name, locale)

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="groups-reactivate-dialog">
        <DialogHeader>
          <DialogTitle>{t('groups.reactivateDialog.title', { name })}</DialogTitle>
          <DialogDescription>
            {t('groups.reactivateDialog.description', { name })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={onClose} disabled={reactivate.isPending}>
            {t('action.cancel')}
          </Button>
          <Button
            loading={reactivate.isPending}
            disabled={reactivate.isPending}
            onClick={() =>
              reactivate.mutate(group.id, {
                onSuccess: () => {
                  toast.success(t('groups.reactivateDialog.success', { name }))
                  onClose()
                },
              })
            }
            data-testid="groups-reactivate-confirm"
          >
            {t('groups.reactivateDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
